/* eslint-disable react/no-unstable-nested-components */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LayoutDashboard, CheckSquare, Users, Network, Settings, Bell,
  Sun, Moon, LogOut, ChevronDown, Menu, Database, UserCog, Lock,
  BarChart3, PanelLeftClose, PanelLeft, Sparkles, Building, Tag, FileSpreadsheet, ShieldCheck, Activity, Layers, Megaphone, KeyRound, FolderKanban,
} from 'lucide-react';
import { toast } from 'sonner';
import { AIAssistantPanel } from './AIAssistantPanel';
import { UniAIWidget } from './UniAIWidget';

const DARK_LOGO = 'https://customer-assets.emergentagent.com/job_uni-work/artifacts/5oxvo39p_United%20Group%20White%20logo%20PNG%20%281%29.png';
const LIGHT_LOGO = 'https://customer-assets.emergentagent.com/job_uni-work/artifacts/eh7i5y2n_United%20Group%20PNG.png';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = ['Marketing', 'Travel', 'Human Resources Management', 'Design & Development', 'Accounts (Credit & Collection)'];

// Clean sidebar nav — no department names. `mod` = access-control module key.
const mainNavItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, mod: 'dashboard' },
  { path: '/tasks', label: 'Tasks', icon: CheckSquare, mod: 'tasks' },
  { path: '/projects', label: 'Projects', icon: FolderKanban, mod: 'projects', projectsAccess: true },
  { path: '/approvals', label: 'Approvals', icon: ShieldCheck, approvalsAccess: true, mod: 'approvals' },
  { path: '/team', label: 'Team', icon: Users, mod: 'team' },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, mod: 'analytics' },
  { path: '/uni-ai', label: 'Uni AI', icon: Sparkles },
  { path: '/frequency', label: 'The Frequency', icon: Megaphone, freqAccess: true, mod: 'frequency' },
];

const superAdminExtras = [
  { path: '/employees', label: 'Employee Master', icon: UserCog },
  { path: '/categories', label: 'Categories', icon: Tag },
  { path: '/sub-categories', label: 'Sub-Categories', icon: Layers },
  { path: '/access-control', label: 'Access Control', icon: KeyRound },
  { path: '/bulk-tasks', label: 'Bulk Tasks', icon: FileSpreadsheet },
  { path: '/activity-logs', label: 'Activity Logs', icon: Activity },
  { path: '/backup', label: 'Backup', icon: Database },
];

export const MainLayout = ({ children }) => {
  const { user, logout, isSuperAdmin, isAdmin, isCoordinator } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [impersonating, setImpersonating] = useState(!!localStorage.getItem('originalToken'));
  const originalUserName = localStorage.getItem('originalUserName') || 'Super Admin';
  const [freqAccess, setFreqAccess] = useState({ has_access: false });
  const isFreqDeptOwner = !!freqAccess?.is_department_owner && !isSuperAdmin && !freqAccess?.can_manage;
  const [moduleAccess, setModuleAccess] = useState(null); // null = not loaded yet (allow all)

  useEffect(() => {
    import('../lib/api').then(({ getFrequencyAccess, getMyAccess }) => {
      getFrequencyAccess().then(r => setFreqAccess(r.data)).catch(() => setFreqAccess({ has_access: false }));
      getMyAccess().then(r => setModuleAccess(r.data.modules || {})).catch(() => setModuleAccess(null));
    });
  }, []);

  const handleExitImpersonation = () => {
    const orig = localStorage.getItem('originalToken');
    if (!orig) return;
    localStorage.setItem('token', orig);
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUserName');
    setImpersonating(false);
    window.location.href = '/employees';
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await getNotifications();
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleMarkRead = async (id) => {
    try { await markNotificationRead(id); fetchNotifications(); }
    catch { toast.error('Failed to mark notification as read'); }
  };

  const handleMarkAllRead = async () => {
    try { await markAllNotificationsRead(); fetchNotifications(); toast.success('All notifications marked as read'); }
    catch { toast.error('Failed to mark all as read'); }
  };

  const getProfileImageUrl = () => {
    if (user?.profile_image) return `${API_URL}/api/users/${user.id}/profile-image`;
    return null;
  };

  const SidebarContent = ({ onLinkClick, collapsed = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`p-4 border-b border-sidebar-border flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
        <Link to="/" className="flex items-center gap-2">
          <img src={theme === 'dark' ? DARK_LOGO : LIGHT_LOGO} alt="United Group" className={collapsed ? "h-8 w-auto" : "h-10 w-auto"} />
        </Link>
        {!collapsed && (
          <Button variant="ghost" size="icon" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="hidden lg:flex">
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Main Nav */}
      <ScrollArea className="flex-1 px-2 py-4">
        <nav className="space-y-1">
          {mainNavItems.filter(item => {
            // Module-level access gating (Super Admin bypasses; null = not loaded yet → allow)
            if (!isSuperAdmin && item.mod && moduleAccess && moduleAccess[item.mod] === false) {
              return false;
            }
            if (item.ddOnly) {
              const myDepts = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
              const isDDContext = isSuperAdmin || myDepts.includes('Design & Development');
              return (isAdmin || isSuperAdmin) && isDDContext;
            }
            if (item.freqAccess) {
              return !!freqAccess?.has_access;
            }
            if (item.projectsAccess) {
              const myDepts = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
              return isSuperAdmin || myDepts.some(d => ['Design & Development', 'Marketing', 'Sales Coordinators'].includes(d));
            }
            if (item.approvalsAccess) {
              const myDepts = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
              const inApprovalDept = isSuperAdmin || myDepts.some(d => ['Design & Development', 'Marketing', 'Sales Coordinators'].includes(d));
              return inApprovalDept && (isSuperAdmin || isAdmin || isCoordinator);
            }
            // Frequency department owners get a Frequency-focused experience — hide Team.
            if (isFreqDeptOwner && item.path === '/team') {
              return false;
            }
            return true;
          }).map(item => {
            const label = item.path === '/tasks' ? 'All Tasks' : item.label;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onLinkClick}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                  location.pathname === item.path
                    ? 'bg-[#F5A623]/10 text-[#F5A623]'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                } ${collapsed ? 'justify-center' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                {!collapsed && <span className="text-sm">{label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Super Admin extras */}
        {isSuperAdmin && (
          <div className="mt-4 pt-4 border-t border-sidebar-border">
            {!collapsed && <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Admin</p>}
            <nav className="space-y-1">
              {superAdminExtras.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onLinkClick}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                    location.pathname === item.path
                      ? 'bg-[#F5A623]/10 text-[#F5A623]'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  } ${collapsed ? 'justify-center' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span className="text-sm">{item.label}</span>}
                </Link>
              ))}
            </nav>
          </div>
        )}

        {/* Admin/Coordinator employee & categories link */}
        {(isAdmin || isCoordinator) && !isSuperAdmin && (
          <div className="mt-4 pt-4 border-t border-sidebar-border">
            <nav className="space-y-1">
              {!isFreqDeptOwner && (
              <Link
                to="/employees"
                onClick={onLinkClick}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                  location.pathname === '/employees'
                    ? 'bg-[#F5A623]/10 text-[#F5A623]'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                } ${collapsed ? 'justify-center' : ''}`}
                data-testid="nav-employees"
              >
                <UserCog className="h-4 w-4 flex-shrink-0" />
                {!collapsed && <span className="text-sm">Team Members</span>}
              </Link>
              )}
              {isAdmin && (
                <Link
                  to="/categories"
                  onClick={onLinkClick}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                    location.pathname === '/categories'
                      ? 'bg-[#F5A623]/10 text-[#F5A623]'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  } ${collapsed ? 'justify-center' : ''}`}
                  data-testid="nav-categories"
                >
                  <Tag className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span className="text-sm">Categories</span>}
                </Link>
              )}
              {isAdmin && ((user?.departments && user.departments.includes('Human Resources Management')) || user?.department === 'Human Resources Management') && (
                <Link
                  to="/sub-categories"
                  onClick={onLinkClick}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                    location.pathname === '/sub-categories'
                      ? 'bg-[#F5A623]/10 text-[#F5A623]'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  } ${collapsed ? 'justify-center' : ''}`}
                  data-testid="nav-sub-categories"
                >
                  <Layers className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span className="text-sm">Sub-Categories</span>}
                </Link>
              )}
              {(isAdmin || isCoordinator) && !isFreqDeptOwner && (
                <Link
                  to="/bulk-tasks"
                  onClick={onLinkClick}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                    location.pathname === '/bulk-tasks'
                      ? 'bg-[#F5A623]/10 text-[#F5A623]'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  } ${collapsed ? 'justify-center' : ''}`}
                  data-testid="nav-bulk-tasks"
                >
                  <FileSpreadsheet className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span className="text-sm">Bulk Tasks</span>}
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/activity-logs"
                  onClick={onLinkClick}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                    location.pathname === '/activity-logs'
                      ? 'bg-[#F5A623]/10 text-[#F5A623]'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  } ${collapsed ? 'justify-center' : ''}`}
                  data-testid="nav-activity-logs"
                >
                  <Activity className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span className="text-sm">Activity Logs</span>}
                </Link>
              )}
            </nav>
          </div>
        )}
      </ScrollArea>

      {/* User Section */}
      <div className="p-3 border-t border-sidebar-border">
        <div className={`flex items-center gap-2 px-2 ${collapsed ? 'justify-center' : ''}`}>
          <Avatar className="h-8 w-8 bg-[#F5A623]/20">
            <AvatarImage src={getProfileImageUrl()} />
            <AvatarFallback className="text-[#F5A623] font-semibold text-sm">{user?.full_name?.charAt(0) || 'U'}</AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col fixed inset-y-0 left-0 bg-sidebar border-r border-sidebar-border z-30 transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}>
        <SidebarContent collapsed={sidebarCollapsed} />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-sidebar">
          <SidebarContent onLinkClick={() => setSidebarOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Impersonation Banner */}
        {impersonating && (
          <div className="sticky top-0 z-30 bg-amber-500 text-black px-4 py-2 flex items-center justify-between" data-testid="impersonation-banner">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="h-4 w-4" />
              <span>Impersonating <strong>{user?.full_name}</strong> · originally logged in as <strong>{originalUserName}</strong></span>
            </div>
            <Button size="sm" variant="outline" className="bg-white/90 hover:bg-white border-black/20 text-black h-7" onClick={handleExitImpersonation} data-testid="exit-impersonation-btn">
              <LogOut className="h-3 w-3 mr-1.5" /> Exit Impersonation
            </Button>
          </div>
        )}
        {/* Top Bar */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between h-14 px-4 lg:px-6">
            {/* Left: mobile menu + collapse */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                {sidebarCollapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
              </Button>

              {/* Department Context */}
              <div className="flex items-center gap-2 ml-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground hidden sm:inline" data-testid="header-department">
                  {isSuperAdmin ? 'All Departments' : ((user?.departments && user.departments.length) ? user.departments.join(', ') : user?.department)}
                </span>
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1">
              {/* AI Assistant */}
              <Button variant="ghost" size="icon" onClick={() => setAiPanelOpen(true)} className="relative" data-testid="ai-assistant-btn">
                <Sparkles className="h-4 w-4 text-[#F5A623]" />
              </Button>

              {/* Theme Toggle */}
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>

              {/* Notifications */}
              <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
                <Button variant="ghost" size="icon" className="relative" onClick={() => setNotifOpen(true)} data-testid="notifications-btn">
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-[#D0021B] rounded-full text-[10px] text-white flex items-center justify-center">{unreadCount}</span>
                  )}
                </Button>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle className="flex items-center justify-between">
                      Notifications
                      {unreadCount > 0 && <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>Mark all read</Button>}
                    </SheetTitle>
                  </SheetHeader>
                  <ScrollArea className="h-[calc(100vh-100px)] mt-4">
                    {notifications.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No notifications</p>
                    ) : (
                      <div className="space-y-2">
                        {notifications.map((notif) => (
                          <div key={notif.id} className={`p-3 rounded-lg cursor-pointer transition-colors ${notif.read ? 'bg-transparent' : 'bg-accent'}`} onClick={() => handleMarkRead(notif.id)} data-testid={`notification-${notif.id}`}>
                            <p className="text-sm">{notif.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </SheetContent>
              </Sheet>

              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="profile-dropdown">
                    <Avatar className="h-7 w-7 bg-[#F5A623]/20">
                      <AvatarImage src={getProfileImageUrl()} />
                      <AvatarFallback className="text-[#F5A623] font-semibold text-xs">{user?.full_name?.charAt(0) || 'U'}</AvatarFallback>
                    </Avatar>
                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div>
                      <p className="font-medium">{user?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      <p className="text-xs text-muted-foreground mt-1">{user?.emp_code}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2" data-testid="settings-link">
                      <Settings className="h-4 w-4" /> Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/change-password" className="flex items-center gap-2" data-testid="change-password-link">
                      <Lock className="h-4 w-4" /> Change Password
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive" data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 bg-[linear-gradient(rgba(245,166,35,0.008)_1px,transparent_1px),linear-gradient(90deg,rgba(245,166,35,0.008)_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(rgba(245,166,35,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(245,166,35,0.02)_1px,transparent_1px)] min-h-[calc(100vh-3.5rem)]">{children}</main>
      </div>

      {/* AI Assistant Panel */}
      <AIAssistantPanel open={aiPanelOpen} onOpenChange={setAiPanelOpen} />

      {/* Uni AI floating voice widget */}
      <UniAIWidget />
    </div>
  );
};
